import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Eye, EyeOff, Loader2, AlertCircle, ArrowLeft, Check } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

// Google logo SVG inline
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

export default function LoginPage() {
  const {
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    loading,
    error,
    clearError,
  } = useAuth();

  // View state: 'main' | 'email-login' | 'email-register' | 'reset-password'
  const [view, setView] = useState("main");

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password) {
      setFormError("Compila tutti i campi.");
      return;
    }

    try {
      await signInWithEmail(email, password);
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password || !confirmPassword) {
      setFormError("Compila tutti i campi.");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Le password non corrispondono.");
      return;
    }

    if (password.length < 6) {
      setFormError("La password deve avere almeno 6 caratteri.");
      return;
    }

    try {
      await signUpWithEmail(email, password, displayName || undefined);
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!email) {
      setFormError("Inserisci la tua email.");
      return;
    }

    try {
      await resetPassword(email);
      setResetSent(true);
    } catch {
      // Error is handled by useAuth
    }
  };

  const goBack = () => {
    setView("main");
    setFormError(null);
    clearError();
    setResetSent(false);
  };

  const displayError = formError || error;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gradient-to-br from-forfy-50 via-white to-neutral-50">
      {/* Card principale */}
      <div className="w-full max-w-md rounded-2xl shadow-xl p-8 bg-white border border-neutral-100">
        {/* Back button for sub-views */}
        {view !== "main" && (
          <button
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 mb-6 -mt-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </button>
        )}

        {/* Logo e titolo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center gap-3 mb-4">
            <img
              src="/landing-assets/logo-forfy.svg"
              alt="Forfy"
              className="w-12 h-12"
            />
            <span className="font-display font-bold text-2xl text-neutral-900">
              forfy
            </span>
          </Link>

          {view === "main" && (
            <p className="mt-3 text-sm text-neutral-500">
              Accedi per gestire la tua P.IVA forfettaria
            </p>
          )}
          {view === "email-login" && (
            <p className="mt-3 text-sm text-neutral-500">
              Accedi con email e password
            </p>
          )}
          {view === "email-register" && (
            <p className="mt-3 text-sm text-neutral-500">
              Crea il tuo account Forfy
            </p>
          )}
          {view === "reset-password" && (
            <p className="mt-3 text-sm text-neutral-500">
              Ti invieremo un link per reimpostare la password
            </p>
          )}
        </div>

        {/* Errore */}
        {displayError && (
          <div className="mb-6 p-4 rounded-lg flex items-start gap-3 bg-red-50 text-red-700">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm">{displayError}</p>
            </div>
          </div>
        )}

        {/* Reset password success */}
        {resetSent && (
          <div className="mb-6 p-4 rounded-lg flex items-start gap-3 bg-emerald-50 text-emerald-700">
            <Check className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm">
                Email inviata! Controlla la tua casella di posta.
              </p>
            </div>
          </div>
        )}

        {/* MAIN VIEW */}
        {view === "main" && (
          <>
            {/* Google button */}
            <button
              onClick={signInWithGoogle}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all border-2 ${
                loading
                  ? "opacity-50 cursor-not-allowed"
                  : "bg-white text-neutral-800 hover:bg-neutral-50 border-neutral-200 hover:border-neutral-300 shadow-sm hover:shadow"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Accesso in corso...</span>
                </>
              ) : (
                <>
                  <GoogleLogo />
                  <span>Continua con Google</span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-neutral-400">oppure</span>
              </div>
            </div>

            {/* Email button */}
            <button
              onClick={() => setView("email-login")}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-medium text-sm bg-neutral-900 text-white hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              <Mail className="w-5 h-5" />
              <span>Continua con Email</span>
            </button>

            {/* Register link */}
            <p className="mt-6 text-center text-sm text-neutral-500">
              Non hai un account?{" "}
              <button
                onClick={() => setView("email-register")}
                className="text-forfy-600 hover:text-forfy-700 font-medium"
              >
                Registrati
              </button>
            </p>
          </>
        )}

        {/* EMAIL LOGIN VIEW */}
        {view === "email-login" && (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300 pr-12"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-medium text-sm bg-forfy-500 text-white hover:bg-forfy-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                "Accedi"
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setView("reset-password");
                setFormError(null);
                clearError();
              }}
              className="w-full text-center text-sm text-neutral-500 hover:text-forfy-600"
            >
              Password dimenticata?
            </button>

            <p className="text-center text-sm text-neutral-500 pt-2">
              Non hai un account?{" "}
              <button
                type="button"
                onClick={() => {
                  setView("email-register");
                  setFormError(null);
                  clearError();
                }}
                className="text-forfy-600 hover:text-forfy-700 font-medium"
              >
                Registrati
              </button>
            </p>
          </form>
        )}

        {/* EMAIL REGISTER VIEW */}
        {view === "email-register" && (
          <form onSubmit={handleEmailRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Nome (opzionale)
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Il tuo nome"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300"
                autoComplete="name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Almeno 6 caratteri"
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300 pr-12"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Conferma password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la password"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-medium text-sm bg-forfy-500 text-white hover:bg-forfy-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creazione account...
                </>
              ) : (
                "Crea account"
              )}
            </button>

            <p className="text-center text-sm text-neutral-500 pt-2">
              Hai già un account?{" "}
              <button
                type="button"
                onClick={() => {
                  setView("email-login");
                  setFormError(null);
                  clearError();
                }}
                className="text-forfy-600 hover:text-forfy-700 font-medium"
              >
                Accedi
              </button>
            </p>
          </form>
        )}

        {/* RESET PASSWORD VIEW */}
        {view === "reset-password" && !resetSent && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-300 focus:border-forfy-300"
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-medium text-sm bg-forfy-500 text-white hover:bg-forfy-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                "Invia link di reset"
              )}
            </button>
          </form>
        )}

        {/* Reset sent - show back to login */}
        {view === "reset-password" && resetSent && (
          <button
            onClick={() => {
              setView("email-login");
              setResetSent(false);
            }}
            className="w-full py-3 rounded-xl font-medium text-sm bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
          >
            Torna al login
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-neutral-400">
          Continuando, accetti i nostri{" "}
          <Link to="/termini" className="text-neutral-500 hover:text-forfy-600">
            Termini di Servizio
          </Link>{" "}
          e la{" "}
          <Link to="/privacy" className="text-neutral-500 hover:text-forfy-600">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
